import {
  Authenticated,
  useCreate,
  useForm,
  useNavigation,
  useTranslate,
} from "@refinedev/core";
import {
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Input,
  List,
  Select,
  Spin,
  Tabs,
  UploadFile,
} from "antd";
import { UploadFilesRef } from "components/uploader/UploadFiles/";
import { AssetLibrary, AssetSource, MediaAsset } from "pages/media/types";
import {
  Dispatch,
  forwardRef,
  Fragment,
  SetStateAction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSortedAssets } from "./edit";
import { DeleteButton, Edit, Show } from "@refinedev/antd";
import {
  PICTURE_PREVIEW_WIDTH,
  PicturePreview,
} from "pages/media/brandkit/components/PicturePreview";
import { AudioSample } from "pages/media/components/AudioSample";
import {
  FONT_PREVIEW_WIDTH,
  FontPreviewCard,
} from "pages/media/brandkit/components/FontPreview";
import VideoPreview, {
  VIDEO_PREVIEW_WIDTH,
} from "pages/media/brandkit/components/VideoPreview";
import SelectTag from "components/SelectTag/";
import styled from "styled-components";
import { LoadingOutlined } from "@ant-design/icons";
import { AssetType } from "./useMediaAssets";
import { getPrettyFontName } from "pages/media/utils";
import { AssetUploader } from "./AssetUploader";
import { AssetMetaFields } from "./AssetMetaFields";
import { StagedAsset } from "components/uploader/types";

export const CREATE_ASSET_REQUEST_TYPE = "CreateLibraryAsset";

const PREVIEW_WIDTH_BY_TYPE: Partial<Record<AssetType, number>> = {
  images: PICTURE_PREVIEW_WIDTH,
  fonts: FONT_PREVIEW_WIDTH,
  video: VIDEO_PREVIEW_WIDTH,
  music: PICTURE_PREVIEW_WIDTH,
};

const EmptyImage = ({
  list,
  translationKey,
}: {
  list: Partial<MediaAsset>[];
  translationKey: string;
}) => {
  const t = useTranslate();
  if (!list || list.length > 0) return null;
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={t(`asset_libraries.${translationKey}`)}
    />
  );
};

const AssetLibraryDetail = forwardRef<
  { handleClickSave: () => void },
  {
    assets: Partial<MediaAsset>[];
    id: string;
    organizationId: string;
    isEdit?: boolean;
    noLayout?: boolean;
    noNavigateAfterSave?: boolean;
    activeTab?: AssetType;
    onUploadComplete?: (assets: Partial<MediaAsset>[]) => void;
    setCurrentAssetLibraryId?: (id: string) => void;
    libraries?: AssetLibrary[];
    isModal?: boolean;
    onDeleteComplete?: (assetId: string) => void;
    hasMore?: boolean;
    fetchNextPage?: () => void;
    setActiveTab?: (tab: AssetType) => void;
  }
>(
  (
    {
      assets,
      id,
      isEdit,
      noLayout,
      noNavigateAfterSave,
      organizationId,
      onUploadComplete,
      setCurrentAssetLibraryId,
      libraries,
      isModal = true,
      onDeleteComplete,
      activeTab,
      hasMore,
      fetchNextPage,
      setActiveTab,
    },
    ref
  ) => {
    const t = useTranslate();
    const { show } = useNavigation();
    const [form] = Form.useForm();
    const [myId, setMyId] = useState<string>(id);
    const uploadPicturesRef = useRef<UploadFilesRef>(null);
    const uploadMusicRef = useRef<UploadFilesRef>(null);
    const uploadFontsRef = useRef<UploadFilesRef>(null);
    const uploadVideoRef = useRef<UploadFilesRef>(null);
    const [activeKey, setActiveKey] = useState<AssetType>(
      activeTab ?? "images"
    );
    const [stagedItems, setStagedItems] = useState<StagedAsset>([]);
    // Local saving state so the Save button shows loading and is disabled while
    // uploading, preventing repeat clicks that would upload the items twice.
    const [saving, setSaving] = useState(false);
    const { images, music, fonts, videos } = useSortedAssets(assets);

    const { onFinish, formLoading, queryResult } = useForm<AssetLibrary>({
      action: "edit",
      resource: `media/asset_libraries`,
      id: myId,
      queryOptions: {
        enabled: !!myId,
      },
    });
    const { mutateAsync: createMediaLibrary } = useCreate<AssetLibrary>();

    if (!queryResult?.data?.data && !formLoading && !myId) {
      // create a new asset library
      setMyId("-");
      createMediaLibrary({
        resource: `media/${organizationId}/asset_libraries`,
        values: {
          name: "Default",
          id: myId,
        },
      })
        .then((response) => {
          if (response) {
            setMyId(response?.data.id);
            setCurrentAssetLibraryId?.(response?.data.id);
          }
        })
        .catch(() => undefined);
    }

    const handleClickSave = async () => {
      if (saving) return;
      setSaving(true);
      try {
        const values = form.getFieldsValue();
        const resource = `media/asset_libraries/${myId}/assets`;
        await Promise.all([
          uploadPicturesRef.current?.upload(resource),
          uploadMusicRef.current?.upload(resource),
          uploadFontsRef.current?.upload(resource),
          uploadVideoRef.current?.upload(resource),
        ]);
        await onFinish(values);
        // Clear the pending upload items (and their staged name/tags) now that
        // they've been uploaded, so they don't linger or get uploaded again.
        uploadPicturesRef.current?.reset();
        uploadMusicRef.current?.reset();
        uploadFontsRef.current?.reset();
        uploadVideoRef.current?.reset();
        setStagedItems([]);
        if (!noNavigateAfterSave) {
          show(`media_asset_libraries`, myId);
        }
      } finally {
        setSaving(false);
      }
    };

    const assetLibrary = queryResult?.data;
    if (form.getFieldValue("name") === undefined && assetLibrary?.data) {
      form.resetFields();
    }

    useImperativeHandle(ref, () => ({
      handleClickSave,
      myId,
    }));

    const assetDeleteButton = useCallback(
      (asset_id: string) => {
        return (
          <DeleteButton
            size="small"
            type="text"
            accessControl={{ enabled: false }}
            hideText
            recordItemId={asset_id}
            resource={`media/asset_libraries/${myId ?? queryResult?.data?.data?.id}/assets`}
            style={{ marginBottom: 5 }}
            onSuccess={() => onDeleteComplete?.(asset_id)}
          />
        );
      },
      [myId, onDeleteComplete, queryResult?.data?.data?.id]
    );

    const renderPreview = useCallback(
      (
        file: UploadFile,
        type: AssetType,
        stagedFiles?: StagedAsset,
        setStagedFiles?: Dispatch<SetStateAction<StagedAsset>>
      ) => {
        const preview = (() => {
          if (type === "images")
            return (
              <PicturePreview
                fileName={file.name}
                key={file.uid}
                image={file.originFileObj}
              />
            );
          if (type === "music")
            return <AudioSample canDelete={false} audio={file.originFileObj} />;
          if (type === "fonts")
            return (
              <FontPreviewCard
                key={file.uid}
                title={"Font"}
                font={file.originFileObj}
              />
            );
          if (type === "video")
            return (
              <VideoPreview
                actions={[]}
                fileName={"Video"}
                video={file.originFileObj}
                minHeight="auto"
              />
            );
          return null;
        })();

        const previewWidth =
          PREVIEW_WIDTH_BY_TYPE[type] ?? PICTURE_PREVIEW_WIDTH;

        return (
          <Flex
            vertical
            gap={8}
            align="center"
            style={{ width: previewWidth, maxWidth: "100%" }}
          >
            {preview}
            <AssetMetaFields
              file={file}
              stagedItems={stagedFiles}
              setStagedItems={setStagedFiles}
            />
          </Flex>
        );
      },
      []
    );
    const tabs = useMemo(
      () => [
        {
          label: t("asset_libraries.images"),
          key: "images",
          children: (
            <ChildrenTabWrapper className="prueba">
              <EmptyImage list={images} translationKey="noImages" />
              {images.length > 0 && (
                <List
                  loading={false}
                  dataSource={images}
                  renderItem={(image) => (
                    <div key={image.id}>
                      <PicturePreview
                        image={image.path}
                        fileName=""
                        meta={false}
                      />
                      {assetDeleteButton(image.id)}
                    </div>
                  )}
                />
              )}
            </ChildrenTabWrapper>
          ),
        },
        {
          label: t("asset_libraries.music"),
          key: "music",
          children: (
            <ChildrenTabWrapper>
              <EmptyImage list={music} translationKey="noMusic" />
              {music.length > 0 && (
                <List
                  loading={false}
                  dataSource={music}
                  renderItem={(audio) => (
                    <Card key={audio.id} size="small" style={{ minWidth: 200 }}>
                      <Flex gap={10} justify="space-between" align="center">
                        <AudioSample canDelete={false} audio={audio.path} />
                        {assetDeleteButton(audio.id)}
                      </Flex>
                    </Card>
                  )}
                />
              )}
            </ChildrenTabWrapper>
          ),
        },
        {
          label: t("asset_libraries.fonts"),
          key: "fonts",
          children: (
            <ChildrenTabWrapper>
              <EmptyImage list={fonts} translationKey="noFonts" />
              {fonts.length > 0 && (
                <List
                  loading={false}
                  dataSource={fonts}
                  renderItem={(font) => (
                    <div key={font.id}>
                      <FontPreviewCard
                        key={font.id}
                        title={getPrettyFontName(font.description)}
                        font={font.path}
                      />
                      {assetDeleteButton(font.id)}
                    </div>
                  )}
                />
              )}
            </ChildrenTabWrapper>
          ),
        },
        {
          label: t("asset_libraries.videos"),
          key: "video",
          children: (
            <ChildrenTabWrapper>
              <EmptyImage list={videos} translationKey="noVideos" />
              {videos.length > 0 && (
                <List
                  grid={{ gutter: 16, column: 5 }}
                  loading={false}
                  locale={{
                    emptyText: t("asset_libraries.noVideos"),
                  }}
                  dataSource={videos}
                  renderItem={(video) => (
                    <Flex key={video.id} vertical gap={5} align="flex-end">
                      <VideoPreview
                        fileName={video.description}
                        video={video.path}
                        actions={[]}
                        minHeight="auto"
                      />
                      {assetDeleteButton(video.id)}
                    </Flex>
                  )}
                />
              )}
            </ChildrenTabWrapper>
          ),
        },
      ],
      [t, images, music, fonts, videos, assetDeleteButton]
    );

    const activeTabUploadProps = useMemo(() => {
      switch (activeKey) {
        case "images":
          return {
            ref: uploadPicturesRef,
            defaultItems: images,
            acceptedMimeTypes: "image/jpeg, image/png, .png, .jpeg, .jpg",
            itemRequestProperties: (file: UploadFile) => ({
              type: CREATE_ASSET_REQUEST_TYPE,
              organization_id: organizationId,
              category: "AssetImage" as const,
              source: AssetSource.UIGenerated,
              description: file?.name,
              asset: {
                type: "Picture" as const,
              },
            }),
            stagedItems,
            setStagedItems,
            renderPreview: (
              file: UploadFile,
              staged?: StagedAsset,
              setStaged?: Dispatch<SetStateAction<StagedAsset>>
            ) => renderPreview(file, "images", staged, setStaged),
          };
        case "music":
          return {
            ref: uploadMusicRef,
            defaultItems: music,
            acceptedMimeTypes: "audio/mp3, .mp3",
            itemRequestProperties: (file: UploadFile) => ({
              type: CREATE_ASSET_REQUEST_TYPE,
              organization_id: organizationId,
              description: file?.name,
              category: "Music" as const,
              source: AssetSource.UIGenerated,
              asset: {
                type: "Picture" as const,
              },
            }),
            stagedItems,
            setStagedItems,
            renderPreview: (
              file: UploadFile,
              staged?: StagedAsset,
              setStaged?: Dispatch<SetStateAction<StagedAsset>>
            ) => renderPreview(file, "music", staged, setStaged),
          };
        case "fonts":
          return {
            ref: uploadFontsRef,
            defaultItems: fonts,
            acceptedMimeTypes: "font/ttf, .ttf",
            itemRequestProperties: (file: File) => ({
              type: CREATE_ASSET_REQUEST_TYPE,
              organization_id: organizationId,
              category: "Font" as const,
              description: file?.name,
              source: AssetSource.UIGenerated,
              asset: {
                type: "Font" as const,
              },
            }),
            stagedItems,
            setStagedItems,
            renderPreview: (
              file: UploadFile,
              staged?: StagedAsset,
              setStaged?: Dispatch<SetStateAction<StagedAsset>>
            ) => renderPreview(file, "fonts", staged, setStaged),
          };
        case "video":
          return {
            ref: uploadVideoRef,
            defaultItems: videos,
            acceptedMimeTypes: "video/mp4, .mp4",
            itemRequestProperties: (file: UploadFile) => ({
              type: CREATE_ASSET_REQUEST_TYPE,
              organization_id: organizationId,
              description: file?.name,
              category: "Video" as const,
              source: AssetSource.UIGenerated,
              asset: {
                type: "Video" as const,
              },
            }),
            stagedItems,
            setStagedItems,
            renderPreview: (
              file: UploadFile,
              staged?: StagedAsset,
              setStaged?: Dispatch<SetStateAction<StagedAsset>>
            ) => renderPreview(file, "video", staged, setStaged),
          };
        default:
          return undefined;
      }
    }, [
      activeKey,
      images,
      music,
      fonts,
      videos,
      organizationId,
      renderPreview,
      stagedItems,
      uploadPicturesRef,
      uploadMusicRef,
      uploadFontsRef,
      uploadVideoRef,
    ]);

    let LayoutComponent = isEdit ? Edit : Show;
    let layoutProps: Record<string, unknown> = {
      isLoading: formLoading,
      canDelete: true,
      saveButtonProps: {
        onClick: handleClickSave,
        loading: saving,
        disabled: saving,
      },
      deleteButtonProps: {
        resource: `media/asset_libraries`,
        resourceItemId: myId,
        accessControl: { enabled: false },
      },
    };
    if (noLayout) {
      LayoutComponent = Fragment;
      layoutProps = {};
    }
    return (
      <Authenticated key="asset-library-detail">
        <LayoutComponent {...layoutProps}>
          <Form
            form={form}
            initialValues={assetLibrary?.data}
            layout="horizontal"
            labelCol={{
              span: 3,
            }}
            wrapperCol={{
              span: 20,
            }}
            labelAlign="right"
          >
            {libraries?.length > 1 && isModal && (
              <Form.Item
                label={t("asset_libraries.select_library")}
                style={{ marginBottom: 10 }}
              >
                <Select
                  optionFilterProp="label"
                  defaultValue={myId}
                  style={{ width: "100%" }}
                  showSearch
                  options={libraries.map((lib) => {
                    return {
                      label: lib.project_id ? (
                        <Flex justify="space-between" align="center">
                          {lib.name}
                          <SelectTag
                            label={t("components.templates.settings.project")}
                            color="black"
                            styles={{ height: 13 }}
                          />
                        </Flex>
                      ) : (
                        <div>{lib.name}</div>
                      ),
                      value: lib.id,
                    };
                  })}
                  onChange={(value) => {
                    setMyId(value);
                    setCurrentAssetLibraryId?.(value);
                    form.setFieldsValue(
                      libraries.find((lib) => lib.id === value)
                    );
                  }}
                />
              </Form.Item>
            )}
            <Form.Item
              label={t("asset_libraries.name")}
              name="name"
              style={{ marginBottom: 10 }}
            >
              <Input />
            </Form.Item>
          </Form>
          <Spin spinning={formLoading} indicator={<LoadingOutlined />}>
            <Tabs
              items={tabs}
              onChange={(tab) => {
                setActiveKey(tab as AssetType);
                setActiveTab?.(tab as AssetType);
              }}
              defaultActiveKey={activeKey}
            />
            {hasMore && (
              <Button style={{ marginBottom: 10 }} onClick={fetchNextPage}>
                Load more
              </Button>
            )}
          </Spin>
          {isEdit && !["stock_photos", "stock_videos"].includes(activeKey) && (
            <AssetUploader
              uploadProps={activeTabUploadProps}
              onUploadComplete={onUploadComplete}
            />
          )}
        </LayoutComponent>
      </Authenticated>
    );
  }
);

const ChildrenTabWrapper = styled.div`
  margin-bottom: 20px;
  .ant-list-items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, max-content));
  }
`;

export default AssetLibraryDetail;
AssetLibraryDetail.displayName = "AssetLibraryDetail";
